import type { PlatformAdapters } from "./adapters";
import { OfflineQueue } from "./queue";
import { Store } from "./store";
import {
  type ActivationConfig,
  type AutoShowConfig,
  type AnnouncementsResponse,
  type ChangelogEntry,
  type ChangelogResponse,
  type FeatureRequest,
  type IdentifyResponse,
  type MessagesResponse,
  type StartMessageResponse,
  type WherdUser,
  type WherdView,
  type ThemeTokens,
  widgetConfigSchema,
} from "./types";
import { uid } from "./util";

export interface WherdClientOptions {
  /** The project's publishable public key (pk_...). */
  publicKey: string;
  /** Wherd API base URL. Defaults to the hosted API; override only for local dev. */
  apiUrl?: string;
  user?: WherdUser;
  /** Developer theme override, merged over server config. */
  theme?: ThemeTokens;
  /** Activation overrides (fab/shake), merged over server config. */
  activation?: Partial<ActivationConfig>;
  /** Auto-show overrides (announcements/changelog), merged over server config. */
  autoShow?: Partial<AutoShowConfig>;
  adapters: PlatformAdapters;
}

export interface SubmitResult {
  ok: boolean;
  queued: boolean;
  id?: string;
  error?: string;
}

const ANON_KEY = "wherd:anonymousId";
const CHANGELOG_SEEN_KEY = "wherd:changelogAutoShown";
const ANNOUNCEMENT_SEEN_KEY = "wherd:announcementAutoShown";
const ANNOUNCEMENT_DISMISSED_KEY = "wherd:announcementsDismissed";
const MESSAGE_SEEN_KEY = "wherd:messagesAutoShown";
const SHAKE_HINT_KEY = "wherd:shakeHintShown";
const DEFAULT_API_URL = "https://api.wherd.io";
const AUTO_SHOW_DEFAULTS: AutoShowConfig = { announcements: true, changelog: false, messages: false };

// The headless brain: identity, config, feedback submission, offline queue,
// and the observable UI store. UI packages render from this; they hold no logic.
export class WherdClient {
  readonly store: Store;
  readonly queue: OfflineQueue;

  private readonly adapters: PlatformAdapters;
  private readonly publicKey: string;
  private readonly apiUrl: string;
  private anonymousId = "";
  private user: WherdUser | null;
  private readonly localAutoShow: Partial<AutoShowConfig> | null;
  // Announcement IDs the user has dismissed; persisted so they never reshow
  // after a reload or new session. Hydrated from storage in init().
  private dismissedAnnouncements = new Set<string>();
  // Closes the live (SSE) connection when set; null when not connected.
  private liveDispose: (() => void) | null = null;

  constructor(opts: WherdClientOptions) {
    this.publicKey = opts.publicKey;
    this.apiUrl = (opts.apiUrl ?? DEFAULT_API_URL).replace(/\/$/, "");
    this.adapters = opts.adapters;
    this.user = opts.user ?? null;
    this.localAutoShow = opts.autoShow ?? null;
    this.queue = new OfflineQueue(opts.adapters.storage);
    this.store = new Store({
      ready: false,
      isOpen: false,
      view: "messages",
      config: null,
      localTheme: opts.theme ?? null,
      localActivation: opts.activation ?? null,
      user: this.user,
      verified: false,
      changelogUnread: 0,
      messages: [],
      messagesUnread: 0,
      announcements: [],
      featureRequestsRevision: 0,
    });
  }

  async init(): Promise<void> {
    let anon = await this.adapters.storage.getItem(ANON_KEY);
    if (!anon) {
      anon = uid("anon_");
      await this.adapters.storage.setItem(ANON_KEY, anon);
    }
    this.anonymousId = anon;

    const dismissed = await this.adapters.storage.getItem(ANNOUNCEMENT_DISMISSED_KEY);
    if (dismissed) {
      try {
        const ids = JSON.parse(dismissed) as string[];
        for (const id of ids) this.dismissedAnnouncements.add(id);
      } catch {
        // corrupt value — start fresh
      }
    }

    await this.refreshConfig();
    await this.flush();
    if (this.user) await this.identify(this.user);
    const changelog = await this.refreshChangelog();
    const messages = await this.refreshMessages();
    await this.refreshAnnouncements();

    this.store.set({ ready: true });

    // Auto-surface new broadcasts once (config-driven; see autoShow()).
    await this.maybeAutoShowAnnouncement();
    await this.maybeAutoShowChangelog(changelog.entries);
    await this.maybeAutoShowMessages(messages);
  }

  // Effective auto-show: server config overridden by the Provider's `autoShow`.
  private autoShow(): AutoShowConfig {
    const cfg = this.store.getSnapshot().config?.autoShow ?? AUTO_SHOW_DEFAULTS;
    // Defaults first so every key stays a boolean even when the server config
    // (or local override) omits the newer `messages` flag.
    return { ...AUTO_SHOW_DEFAULTS, ...cfg, ...(this.localAutoShow ?? {}) };
  }

  // Auto-open the widget when a new active announcement is available (once per
  // announcement). On by default — announcements are time-sensitive.
  private async maybeAutoShowAnnouncement(): Promise<void> {
    if (!this.autoShow().announcements) return;
    const active = this.store.getSnapshot().announcements;
    const newest = active[0];
    if (!newest) return;
    const marker = newest.publishedAt ?? newest.id;
    const seen = await this.adapters.storage.getItem(ANNOUNCEMENT_SEEN_KEY);
    if (seen === marker) return;
    await this.adapters.storage.setItem(ANNOUNCEMENT_SEEN_KEY, marker);
    this.open();
  }

  // Opens "What's New" automatically the first time a newer published entry is
  // seen. Off by default (changelog is less urgent); enable via autoShow.
  private async maybeAutoShowChangelog(entries: ChangelogEntry[]): Promise<void> {
    if (!this.autoShow().changelog) return;
    const newest = entries.find((e) => e.publishedAt) ?? entries[0];
    if (!newest) return;
    const marker = newest.publishedAt ?? newest.id;
    const seen = await this.adapters.storage.getItem(CHANGELOG_SEEN_KEY);
    if (seen === marker) return;
    await this.adapters.storage.setItem(CHANGELOG_SEEN_KEY, marker);
    // Only interrupt the user when there's genuinely something unread.
    if (this.store.getSnapshot().changelogUnread > 0) this.open("changelog");
  }

  // Auto-open the panel to messages when a new unread admin reply arrives.
  // Off by default (opt-in via autoShow) — a reply is a direct response, so the
  // interrupt is warranted, but only once per reply. The marker is the newest
  // reply timestamp, so it re-fires when a genuinely newer reply lands.
  private async maybeAutoShowMessages(data: MessagesResponse): Promise<void> {
    if (!this.autoShow().messages) return;
    if (data.unreadTotal <= 0) return;
    let marker = "";
    for (const thread of data.threads) {
      for (const reply of thread.replies) {
        if (reply.createdAt > marker) marker = reply.createdAt;
      }
    }
    if (!marker) return;
    const seen = await this.adapters.storage.getItem(MESSAGE_SEEN_KEY);
    if (seen === marker) return;
    await this.adapters.storage.setItem(MESSAGE_SEEN_KEY, marker);
    this.open("messages");
  }

  // Open a live (SSE) connection so data updates push in real time. Idempotent:
  // re-opening closes any prior connection (used to re-key on identity change).
  // No-op when no eventSource adapter is wired (the SDK stays pull-only).
  // Returns an unsubscribe that closes the connection.
  subscribeLive(): () => void {
    this.disconnectLive();
    const adapter = this.adapters.eventSource;
    if (!adapter) return () => {};
    const params = new URLSearchParams({
      key: this.publicKey,
      anonymousId: this.anonymousId,
    });
    if (this.user?.id) params.set("externalId", this.user.id);
    this.liveDispose = adapter.connect(
      `${this.apiUrl}/v1/stream?${params.toString()}`,
      (type) => void this.handleLiveEvent(type),
    );
    return () => this.disconnectLive();
  }

  disconnectLive(): void {
    this.liveDispose?.();
    this.liveDispose = null;
  }

  // React to a live signal by refetching the named resource (and re-running
  // auto-show, mirroring the foreground path).
  private async handleLiveEvent(type: string): Promise<void> {
    switch (type) {
      case "messages": {
        const data = await this.refreshMessages();
        await this.maybeAutoShowMessages(data);
        break;
      }
      case "announcements": {
        await this.refreshAnnouncements();
        await this.maybeAutoShowAnnouncement();
        break;
      }
      case "changelog": {
        const data = await this.refreshChangelog();
        await this.maybeAutoShowChangelog(data.entries);
        break;
      }
      case "featureRequests": {
        const rev = this.store.getSnapshot().featureRequestsRevision + 1;
        this.store.set({ featureRequestsRevision: rev });
        break;
      }
      // "ping" (heartbeat) and unknown types are ignored.
    }
  }

  // Refetch everything the store tracks (messages, announcements, changelog)
  // and re-run auto-show. Called on app foreground so all data catches up — and
  // auto-opens if a new item warrants it — after the app was backgrounded.
  async foregroundRefresh(): Promise<void> {
    const [messages, changelog] = await Promise.all([
      this.refreshMessages(),
      this.refreshChangelog(),
      this.refreshAnnouncements(),
    ]);
    await this.maybeAutoShowAnnouncement();
    await this.maybeAutoShowChangelog(changelog.entries);
    await this.maybeAutoShowMessages(messages);
  }

  // One-time shake-to-open hint persistence (used by the RN widget).
  async shouldShowShakeHint(): Promise<boolean> {
    return (await this.adapters.storage.getItem(SHAKE_HINT_KEY)) == null;
  }
  async dismissShakeHint(): Promise<void> {
    await this.adapters.storage.setItem(SHAKE_HINT_KEY, "1");
  }

  private headers(): Record<string, string> {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.publicKey}`,
    };
  }

  getAnonymousId(): string {
    return this.anonymousId;
  }

  async refreshConfig(): Promise<void> {
    try {
      const res = await fetch(`${this.apiUrl}/v1/config`, { headers: this.headers() });
      if (res.ok) {
        const config = widgetConfigSchema.parse(await res.json());
        this.store.set({ config });
      }
    } catch {
      // Config is best-effort; UI falls back to its own defaults.
    }
  }

  async identify(user: WherdUser): Promise<void> {
    this.user = user;
    this.store.set({ user });
    try {
      const res = await fetch(`${this.apiUrl}/v1/identify`, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({
          anonymousId: this.anonymousId,
          externalId: user.id,
          email: user.email,
          name: user.name,
          traits: user.traits,
          userHash: user.userHash,
        }),
      });
      if (res.ok) {
        const json = (await res.json()) as IdentifyResponse;
        this.store.set({ verified: json.verified });
      }
    } catch {
      // Identity is non-fatal; feedback still works anonymously.
    }
    // Re-key the live stream to the new identity if it's already open.
    if (this.liveDispose) this.subscribeLive();
  }

  // ---- Messages (two-way threads) ----

  // Start a new thread (formerly submitFeedback).
  async startMessage(input: {
    body: string;
    metadata?: Record<string, unknown>;
  }): Promise<SubmitResult> {
    const payload: Record<string, unknown> = {
      anonymousId: this.anonymousId,
      externalId: this.user?.id,
      body: input.body,
      metadata: { ...this.adapters.device.getMetadata(), ...(input.metadata ?? {}) },
    };

    try {
      const res = await fetch(`${this.apiUrl}/v1/messages`, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const json = (await res.json()) as StartMessageResponse;
        void this.refreshMessages();
        return { ok: true, queued: false, id: json.id };
      }
      // Client errors (4xx) are not retryable — surface them.
      if (res.status < 500) {
        return { ok: false, queued: false, error: `Request failed (${res.status})` };
      }
      // Server errors → queue for retry.
      await this.queue.enqueue("messages", payload);
      return { ok: true, queued: true };
    } catch {
      // Offline / network error → queue for retry.
      await this.queue.enqueue("messages", payload);
      return { ok: true, queued: true };
    }
  }

  // The end-user's own threads (+ unread admin replies). Updates the badge.
  async refreshMessages(): Promise<MessagesResponse> {
    try {
      const res = await fetch(`${this.apiUrl}/v1/messages?${this.identityQuery()}`, {
        headers: this.headers(),
      });
      if (res.ok) {
        const data = (await res.json()) as MessagesResponse;
        this.store.set({ messages: data.threads, messagesUnread: data.unreadTotal });
        return data;
      }
    } catch {
      // offline — keep last known threads + unread count
    }
    const snap = this.store.getSnapshot();
    return { threads: snap.messages, unreadTotal: snap.messagesUnread };
  }

  async replyToMessage(messageId: string, body: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.apiUrl}/v1/messages/${messageId}/replies`, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({
          anonymousId: this.anonymousId,
          externalId: this.user?.id,
          body,
        }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  async markMessageRead(messageId: string): Promise<void> {
    try {
      await fetch(`${this.apiUrl}/v1/messages/${messageId}/read`, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({
          anonymousId: this.anonymousId,
          externalId: this.user?.id,
        }),
      });
      void this.refreshMessages();
    } catch {
      // ignore — reconciles on next refresh
    }
  }

  // ---- Announcements ----

  async refreshAnnouncements(): Promise<void> {
    try {
      const res = await fetch(
        `${this.apiUrl}/v1/announcements?${this.identityQuery()}`,
        { headers: this.headers() },
      );
      if (res.ok) {
        const data = (await res.json()) as AnnouncementsResponse;
        this.store.set({
          announcements: data.announcements.filter(
            (a) => !this.dismissedAnnouncements.has(a.id),
          ),
        });
      }
    } catch {
      // offline — keep last known announcements
    }
  }

  async dismissAnnouncement(announcementId: string): Promise<void> {
    // Optimistically remove from the banner stack.
    this.store.set({
      announcements: this.store
        .getSnapshot()
        .announcements.filter((a) => a.id !== announcementId),
    });
    // Remember the dismissal locally so it stays hidden across reloads even if
    // the server keeps returning it (e.g. anonymous identity not yet linked).
    this.dismissedAnnouncements.add(announcementId);
    await this.adapters.storage.setItem(
      ANNOUNCEMENT_DISMISSED_KEY,
      JSON.stringify([...this.dismissedAnnouncements]),
    );
    try {
      await fetch(`${this.apiUrl}/v1/announcements/${announcementId}/dismiss`, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({
          anonymousId: this.anonymousId,
          externalId: this.user?.id,
        }),
      });
    } catch {
      // ignore — already removed locally
    }
  }

  // ---- Feature requests ----

  private identityQuery(): string {
    const params = new URLSearchParams({ anonymousId: this.anonymousId });
    if (this.user?.id) params.set("externalId", this.user.id);
    return params.toString();
  }

  async listFeatureRequests(): Promise<FeatureRequest[]> {
    try {
      const res = await fetch(
        `${this.apiUrl}/v1/feature-requests?${this.identityQuery()}`,
        { headers: this.headers() },
      );
      if (res.ok) return (await res.json()) as FeatureRequest[];
    } catch {
      // offline — return empty; UI can show a retry.
    }
    return [];
  }

  async createFeatureRequest(input: {
    title: string;
    body?: string;
  }): Promise<FeatureRequest | null> {
    try {
      const res = await fetch(`${this.apiUrl}/v1/feature-requests`, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({
          anonymousId: this.anonymousId,
          externalId: this.user?.id,
          ...input,
        }),
      });
      if (res.ok) return (await res.json()) as FeatureRequest;
    } catch {
      // ignore
    }
    return null;
  }

  async voteFeatureRequest(
    requestId: string,
    voted: boolean,
  ): Promise<{ voted: boolean; voteCount: number } | null> {
    try {
      const res = await fetch(
        `${this.apiUrl}/v1/feature-requests/${requestId}/vote`,
        {
          method: voted ? "POST" : "DELETE",
          headers: this.headers(),
          body: JSON.stringify({
            anonymousId: this.anonymousId,
            externalId: this.user?.id,
          }),
        },
      );
      if (res.ok) return (await res.json()) as { voted: boolean; voteCount: number };
    } catch {
      // ignore
    }
    return null;
  }

  // ---- Changelog / What's New ----

  async refreshChangelog(): Promise<ChangelogResponse> {
    try {
      const res = await fetch(
        `${this.apiUrl}/v1/changelog?${this.identityQuery()}`,
        { headers: this.headers() },
      );
      if (res.ok) {
        const data = (await res.json()) as ChangelogResponse;
        this.store.set({ changelogUnread: data.unreadCount });
        return data;
      }
    } catch {
      // offline — keep last known unread count
    }
    return { entries: [], unreadCount: this.store.getSnapshot().changelogUnread };
  }

  async markChangelogRead(entryId: string): Promise<void> {
    try {
      await fetch(`${this.apiUrl}/v1/changelog/${entryId}/read`, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({
          anonymousId: this.anonymousId,
          externalId: this.user?.id,
        }),
      });
    } catch {
      // ignore — will reconcile on next refresh
    }
  }

  async flush(): Promise<void> {
    const items = await this.queue.list();
    for (const item of items) {
      try {
        const res = await fetch(`${this.apiUrl}/v1/${item.type}`, {
          method: "POST",
          headers: this.headers(),
          body: JSON.stringify(item.payload),
        });
        if (res.ok || (res.status >= 400 && res.status < 500)) {
          // Success or permanently-rejected → drop from queue.
          await this.queue.remove(item.id);
        }
      } catch {
        // Still offline — stop and retry on next flush.
        break;
      }
    }
  }

  open(view?: WherdView): void {
    this.store.set({ isOpen: true, view: view ?? this.store.getSnapshot().view });
  }

  close(): void {
    this.store.set({ isOpen: false });
  }

  setView(view: WherdView): void {
    this.store.set({ view });
  }

  // Update the developer theme override at runtime (e.g. the host app toggled
  // its own light/dark appearance). Re-resolves on the next render.
  setTheme(theme: ThemeTokens | null): void {
    this.store.set({ localTheme: theme });
  }
}
